// ============================================================================
// Dashboard (spec §8.2): stats tiles (XP/level/streak/mastered), activation
// banner for pending accounts, and entry points into the study loop.
// ============================================================================

import { el, toast } from '../dom.js';
import { getState } from '../state/store.js';
import * as api from '@core/data/api.js';

function tile(value, label) {
  return el('div.tile', {}, [
    el('div.tile__value', { text: String(value ?? 0) }),
    el('div.tile__label', { text: label }),
  ]);
}

function badgeChip(b) {
  return el(`div.badge-chip${b.earned ? '.badge-chip--earned' : ''}`, { title: `${b.name} — ${b.description}` }, [
    el('div.badge-chip__icon', { text: b.icon || '🏅' }),
    el('div.badge-chip__name', { text: b.name }),
    el('div.faint', { text: b.earned ? 'Earned ✓' : `${b.progress}/${b.target}` }),
  ]);
}

function paintInsights(card, ins, navigate) {
  const m = ins.mastery || {};
  const due = ins.due || {};
  const leeches = ins.leeches || [];
  const masteryTiles = [['new', 'New'], ['learning', 'Learning'], ['familiar', 'Familiar'], ['mastered', 'Mastered'], ['lapsed', 'Lapsed']]
    .map(([k, label]) => tile(m[k] || 0, label));
  const dueTiles = [tile(due.overdue || 0, 'Overdue'), tile(due.today || 0, 'Due today'), tile(due.next7 || 0, 'Next 7 days')];
  const leechBody = leeches.length
    ? el('div.row', { style: 'flex-wrap:wrap;gap:var(--space-2)' }, leeches.map((l) =>
        el('button.btn', { title: `${l.lapses} lapses · ${l.meaning_en || ''}`, onclick: () => navigate(`#/item/${l.item_type}/${l.item_id}`) },
          [el('span', { style: 'font-family:var(--font-hanja);font-size:1.2rem', text: l.item_type === 'character' ? l.char : (l.hangul || l.hanja_form) })])))
    : el('p.faint', { text: 'No sticky items — nice! Anything you keep lapsing on shows up here.' });

  card.replaceChildren(
    el('h2', { text: 'Insights' }),
    el('div.faint', { text: 'Mastery' }),
    el('div.grid', {}, masteryTiles),
    el('div.faint', { text: 'Review forecast' }),
    el('div.grid', {}, dueTiles),
    el('div.faint', { text: 'Sticky items (leeches)' }),
    leechBody,
  );
}

function paintBadges(card, badges) {
  const earned = badges.filter((b) => b.earned).length;
  card.replaceChildren(
    el('div.row', {}, [el('h2', { text: 'Badges' }), el('div.appbar__spacer'), el('span.faint', { text: `${earned}/${badges.length} earned` })]),
    el('div.badge-grid', {}, badges.map(badgeChip)),
  );
}

export function renderDashboard({ navigate }) {
  const { profile, stats } = getState();
  const container = el('div.container.stack');

  // Activation state (spec §4). Pending accounts can preview but not yet study.
  if (profile && profile.status !== 'active') {
    container.append(
      el('div.banner.banner--warn', {}, [
        el('strong', { text: 'Account pending activation. ' }),
        el('span', {
          text:
            profile.status === 'deactivated'
              ? 'This account has been deactivated. Contact an admin.'
              : 'A tutor or admin needs to activate your account before you can save study progress. You can still browse content.',
        }),
      ])
    );
  }

  container.append(
    el('div.card.stack', {}, [
      el('h1', { text: `Welcome${profile?.display_name && profile.display_name !== 'New Learner' ? ', ' + profile.display_name : ''}` }),
      el('div.grid', {}, [
        tile(stats?.xp_total, 'XP'),
        tile(stats?.level, 'Level'),
        tile(stats?.current_streak, 'Day streak'),
        tile(stats?.hanja_mastered, 'Hanja mastered'),
        tile(stats?.words_mastered, 'Words mastered'),
      ]),
    ])
  );

  const studyBtn = el('button.btn.btn--primary', {
    text: 'Start studying →',
    onclick: () => navigate('#/study'),
    disabled: profile && profile.status !== 'active',
  });

  container.append(
    el('div.card.stack', {}, [
      el('h2', { text: 'Study' }),
      el('p.muted', { text: 'Review with spaced repetition. Due items and new characters are interleaved automatically.' }),
      el('div.row', {}, [studyBtn]),
    ])
  );

  // Live due-count (best effort; requires an active account + RLS access).
  if (profile?.status === 'active') {
    api.dueQueue(50).then((items) => {
      const n = Array.isArray(items) ? items.length : 0;
      studyBtn.textContent = n ? `Study ${n} item${n === 1 ? '' : 's'} →` : 'Start studying →';
    }).catch(() => {});

    // Insights: mastery breakdown, review forecast, and leeches.
    const insightsCard = el('div.card.stack', {}, [el('h2', { text: 'Insights' }), el('p.faint', { text: 'Loading…' })]);
    container.append(insightsCard);
    api.insights()
      .then((ins) => paintInsights(insightsCard, ins, navigate))
      .catch(() => insightsCard.replaceChildren(el('h2', { text: 'Insights' }), el('p.muted', { text: 'Could not load insights.' })));

    // Badges: award any newly-earned, then render the catalog with progress.
    const badgesCard = el('div.card.stack', {}, [el('h2', { text: 'Badges' }), el('p.faint', { text: 'Loading…' })]);
    container.append(badgesCard);
    (async () => {
      try {
        const newly = await api.checkBadges().catch(() => []);
        const badges = await api.myBadges();
        paintBadges(badgesCard, badges);
        if (newly.length) {
          const names = badges.filter((b) => newly.includes(b.code)).map((b) => `${b.icon} ${b.name}`);
          toast(`New badge${newly.length > 1 ? 's' : ''}: ${names.join(', ')}`, 'success');
        }
      } catch {
        badgesCard.replaceChildren(el('h2', { text: 'Badges' }), el('p.muted', { text: 'Could not load badges.' }));
      }
    })();
  }

  return container;
}
